// `focusMode`/`focusDistance` are real Chromium extensions to the Media Capture
// and Streams spec, but aren't part of TS's standard lib.dom.d.ts typings.
export interface FocusCapabilities extends MediaTrackCapabilities {
  focusMode?: string[]
  focusDistance?: { min?: number; max?: number; step?: number }
}

export const getBackCameraDevices = (
  devices: MediaDeviceInfo[],
): MediaDeviceInfo[] =>
  devices.filter((d) => d.kind === "videoinput" && !/front/i.test(d.label))

export type CameraCapabilityProbe = (
  deviceId: string,
) => Promise<FocusCapabilities | null>

export const probeCameraCapabilities: CameraCapabilityProbe = async (
  deviceId,
) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } },
    })
    const track = stream.getVideoTracks()[0]
    const capabilities = track.getCapabilities() as FocusCapabilities
    track.stop()
    return capabilities
  } catch {
    return null
  }
}

const hasLabeledVideoInput = (devices: MediaDeviceInfo[]): boolean =>
  devices.some((d) => d.kind === "videoinput" && d.label !== "")

export const enumerateLabeledVideoInputs = async (): Promise<
  MediaDeviceInfo[]
> => {
  const devices = await navigator.mediaDevices.enumerateDevices()

  // Labels are already populated, which only happens once camera permission
  // has been granted — no need to open a priming stream just to ask again.
  if (hasLabeledVideoInput(devices)) {
    return devices
  }

  try {
    const primingStream = await navigator.mediaDevices.getUserMedia({
      video: true,
    })
    // We only opened the camera to ask for permission, not to actually use it.
    // Turn it off now — otherwise the camera light stays on, the device stays
    // locked (the real scan stream opened below could fail to start), and the
    // camera keeps draining battery for no reason.
    primingStream.getTracks().forEach((track) => track.stop())
  } catch {
    // Permission denied or unavailable — return what we already enumerated,
    // likely unlabeled.
    return devices
  }

  return navigator.mediaDevices.enumerateDevices()
}

type ScoredCamera = {
  deviceId: string
  capabilities: FocusCapabilities | null
}

const isContinuousAutofocusCapable = (scored: ScoredCamera): boolean =>
  scored.capabilities?.focusMode?.includes("continuous") ?? false

const byClosestFocusDistance = (a: ScoredCamera, b: ScoredCamera) =>
  (a.capabilities?.focusDistance?.min ?? Infinity) -
  (b.capabilities?.focusDistance?.min ?? Infinity)

const rankByFocusDistance = (scored: ScoredCamera[]): string =>
  [...scored].sort(byClosestFocusDistance)[0].deviceId

const probeBackCamerasSequentially = async (
  backCameras: MediaDeviceInfo[],
  probeCapabilities: CameraCapabilityProbe,
): Promise<ScoredCamera[]> => {
  const scored: ScoredCamera[] = []

  for (const d of backCameras) {
    if (!d.deviceId) continue
    // Defends against a probe implementation that rejects instead of
    // resolving to null (probeCameraCapabilities itself already catches,
    // but callers shouldn't have to rely on that).
    const capabilities = await probeCapabilities(d.deviceId).catch(() => null)
    scored.push({ deviceId: d.deviceId, capabilities })
  }

  return scored
}

export const selectBestBackCamera = async (
  devices: MediaDeviceInfo[],
  probeCapabilities: CameraCapabilityProbe,
): Promise<string | null> => {
  const backCameras = getBackCameraDevices(devices)

  if (backCameras.length === 1) {
    return backCameras[0].deviceId || null
  }

  const scored = await probeBackCamerasSequentially(
    backCameras,
    probeCapabilities,
  )

  const autofocusCapable = scored.filter(isContinuousAutofocusCapable)

  return autofocusCapable.length > 0
    ? rankByFocusDistance(autofocusCapable)
    : null
}
